/**
 * Dropdown Stimulus Controller
 *
 * This Stimulus controller provides interactive functionality for the Dropdown component.
 * All event handling is done through Stimulus data attributes in the HTML template.
 */

import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

class Dropdown extends Controller {
  static targets = ['menu', 'trigger'];
  static values = {
    open: Boolean,
  };

  declare readonly menuTarget: HTMLElement;
  declare readonly triggerTarget: HTMLElement;
  declare readonly hasMenuTarget: boolean;
  declare readonly hasTriggerTarget: boolean;
  declare openValue: boolean;

  private closeTimeout?: number;
  private isHoveringTrigger = false;
  private isHoveringMenu = false;

  /**
   * Called when the controller is connected to the DOM
   */
  connect() {
    // Initialize state
    this.openValue = false;
  }

  /**
   * Called when the controller is disconnected from the DOM
   */
  disconnect() {
    this.clearCloseTimeout();
  }

  /**
   * Toggle the dropdown menu
   */
  toggle() {
    this.openValue = !this.openValue;
  }

  /**
   * Open the dropdown menu
   */
  open() {
    this.openValue = true;
  }

  /**
   * Close the dropdown menu
   */
  close() {
    this.openValue = false;
  }

  /**
   * Handle changes to the open state
   */
  openValueChanged() {
    if (this.hasMenuTarget) {
      if (this.openValue) {
        this.showMenu();
      } else {
        this.hideMenu();
      }
    }
  }

  /**
   * Handle clicks outside the dropdown (called via data-action)
   */
  handleOutsideClick(event: Event) {
    const target = event.target as Element;

    // Only close if the dropdown is open and the click is truly outside
    if (this.openValue && !this.triggerTarget.contains(target)) {
      this.close();
    }
  }

  /**
   * Handle escape key press (called via data-action)
   */
  handleEscape(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Handle mouse entering the trigger
   */
  enterTrigger() {
    this.isHoveringTrigger = true;
    this.clearCloseTimeout();
    this.open();
  }

  /**
   * Handle mouse leaving the trigger
   */
  leaveTrigger() {
    this.isHoveringTrigger = false;
    this.scheduleCloseIfNotHovering();
  }

  /**
   * Handle mouse entering the menu
   */
  enterMenu() {
    this.isHoveringMenu = true;
    this.clearCloseTimeout();
  }

  /**
   * Handle mouse leaving the menu
   */
  leaveMenu() {
    this.isHoveringMenu = false;
    this.scheduleCloseIfNotHovering();
  }

  /**
   * Schedule closing the dropdown after a delay (for hover trigger)
   */
  scheduleClose() {
    this.closeTimeout = window.setTimeout(() => {
      this.close();
    }, 300); // 300ms delay
  }

  /**
   * Schedule close only if not hovering over trigger or menu
   */
  private scheduleCloseIfNotHovering() {
    // Only schedule close if we're not hovering over either trigger or menu
    if (!this.isHoveringTrigger && !this.isHoveringMenu) {
      this.scheduleClose();
    }
  }

  /**
   * Cancel the scheduled close (for hover trigger)
   */
  cancelClose() {
    this.clearCloseTimeout();
  }

  /**
   * Clear the close timeout
   */
  private clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = undefined;
    }
  }

  /**
   * Show the dropdown menu
   */
  private showMenu() {
    this.menuTarget.classList.remove('hidden');

    // Dispatch custom event
    this.dispatch('opened', { detail: { dropdown: this.element } });
  }

  /**
   * Hide the dropdown menu
   */
  private hideMenu() {
    this.menuTarget.classList.add('hidden');

    // Reset hover states when hiding
    this.isHoveringTrigger = false;
    this.isHoveringMenu = false;

    // Dispatch custom event
    this.dispatch('closed', { detail: { dropdown: this.element } });
  }
}

application.register('ui--dropdown', Dropdown);
